<script setup>
/**
 * OCHAT: cargar cupo y tarifas de una temporada de una sola vez.
 *
 * Así habla un hotelero: "del 1 de diciembre al 28 de febrero, doble, 8 habitaciones
 * a 80.000 la noche, mínimo 2 noches". Este formulario es esa frase.
 *
 * EL RANGO ES INCLUSIVO, AL REVÉS QUE UNA ESTADÍA
 * Una reserva del 1 al 28 son 27 noches. Una carga del 1 al 28 son 28 noches de
 * tarifa. Acá manda la segunda regla, porque es la que el hotelero espera cuando
 * elige "del 1 al 28 de febrero". Se dice en pantalla para que no haya que deducirlo.
 *
 * SOLO SE ESCRIBE LO QUE SE COMPLETA
 * El servicio del servidor acota el UPDATE a los campos que llegan, así que dejar un
 * campo vacío NO lo pone en cero: lo deja como estaba. Eso es lo que permite subir
 * el precio de un fin de semana sin pisar el mínimo de noches que ya estaba cargado.
 * Por eso los booleanos son desplegables de tres estados y no casillas: una casilla
 * no puede decir "no lo toques".
 *
 * `closed` EXISTE EN LAS DOS MITADES Y NO ES LO MISMO
 * En cupo cierra el TIPO DE HABITACIÓN a la venta; en tarifas cierra ESE PLAN. Los
 * textos son distintos a propósito: son dos columnas distintas de dos tablas
 * distintas, y confundirlas cierra el hotel entero cuando se quería cerrar una tarifa.
 *
 * `sold` y `held` no aparecen acá ni pueden aparecer: son el resultado de las
 * reservas, no configuración.
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import WeekdayPicker from './WeekdayPicker.vue';

const props = defineProps({
  property: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();
const uiFlags = useMapGetter('bookingSettings/getUIFlags');

const todayISO = () => new Date().toLocaleDateString('en-CA');

const range = ref({ from: todayISO(), to: todayISO(), weekdays: [] });
const roomTypeId = ref(null);
const ratePlanId = ref(null);

const inventory = ref({ allotment: '', closed: '' });
const rates = ref({
  price_base: '',
  price_extra_adult: '',
  price_extra_child: '',
  min_los: '',
  max_los: '',
  closed_to_arrival: '',
  closed_to_departure: '',
  closed: '',
});

// Aparte del resto porque no es un valor sino la intención de borrar uno. Ver el
// comentario en ratesPayload.
const clearMaxLos = ref(false);

const roomTypes = computed(() => props.property.room_types ?? []);

const selectedRoomType = computed(() =>
  roomTypes.value.find(item => item.id === Number(roomTypeId.value))
);

// Los planes son de un tipo de habitación, así que el desplegable se acota al tipo
// elegido. Ofrecer todos los planes del hotel dejaría elegir una combinación que el
// servidor rechaza.
const ratePlans = computed(() => selectedRoomType.value?.rate_plans ?? []);

/**
 * Deja la selección apuntando a algo que exista: si lo elegido sigue en la lista se
 * respeta, y si no se cae al primero.
 *
 * SE RECONCILIA CONTRA LA LISTA, NO SE ENCADENAN WATCHERS
 * Antes esto eran dos watchers en cadena: uno con `immediate` sobre `roomTypes` que
 * elegía el primer tipo, y otro sobre `roomTypeId` que de ahí derivaba el plan. No
 * funcionaba nunca. Un watcher con `immediate` corre DURANTE el setup, así que el
 * primero mutaba `roomTypeId` antes de que el segundo existiera, y el segundo no
 * veía ese cambio: `ratePlanId` se quedaba en null para siempre. El síntoma era el
 * desplegable de planes en blanco y el botón de aplicar muerto sin decir por qué.
 *
 * Reconciliar contra la lista cubre los tres casos con la misma línea: el montaje,
 * el cambio de tipo de habitación, y que borren el plan que estaba elegido.
 */
const reconcile = (current, list) =>
  list.some(item => item.id === current) ? current : (list[0]?.id ?? null);

watch(
  roomTypes,
  list => {
    roomTypeId.value = reconcile(roomTypeId.value, list);
  },
  { immediate: true }
);

// `ratePlans` deriva de `roomTypeId`, así que este watcher cubre también el cambio
// de tipo de habitación. Va declarado después del de arriba a propósito: con
// `immediate` los dos, el orden de registro es el orden de ejecución, y este necesita
// que `roomTypeId` ya esté resuelto.
watch(
  ratePlans,
  list => {
    ratePlanId.value = reconcile(ratePlanId.value, list);
  },
  { immediate: true }
);

/**
 * Cuántas noches toca la carga. Se calcula igual que en el servidor —rango inclusivo
 * y filtro por día de semana— para que el número que se muestra antes de aplicar sea
 * el mismo que el que vuelve después.
 *
 * Se trabaja en UTC a propósito: con fechas locales, un usuario al oeste de Greenwich
 * puede ver un día de menos por el corrimiento horario.
 */
const affectedNights = computed(() => {
  const { from, to, weekdays } = range.value;
  if (!from || !to) return 0;

  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  const start = Date.UTC(fy, fm - 1, fd);
  const end = Date.UTC(ty, tm - 1, td);
  if (end < start) return 0;

  const days = weekdays.length ? weekdays : [0, 1, 2, 3, 4, 5, 6];
  let count = 0;
  for (let stamp = start; stamp <= end; stamp += 86400000) {
    if (days.includes(new Date(stamp).getUTCDay())) count += 1;
  }
  return count;
});

const rangeIsValid = computed(() => affectedNights.value > 0);

/** Deja solo los campos que el usuario realmente completó. */
const filled = source =>
  Object.entries(source).reduce((result, [key, value]) => {
    if (value === '' || value === null) return result;
    if (value === 'true' || value === 'false') {
      return { ...result, [key]: value === 'true' };
    }
    return { ...result, [key]: value };
  }, {});

const inventoryPayload = computed(() => filled(inventory.value));

/**
 * `max_los` es la única columna que admite nulo, y eso abre un caso que la regla de
 * "solo se escribe lo que se completa" no puede expresar: QUITAR un máximo ya
 * cargado. Dejar el campo vacío significa "no lo toques", así que sin esta casilla el
 * hotelero que puso "máximo 5 noches" en temporada alta no podría levantarlo nunca
 * más desde la pantalla.
 *
 * Se manda `null` explícito, que es lo que el servicio escribe en la columna.
 */
const ratesPayload = computed(() => {
  const payload = filled(rates.value);
  if (clearMaxLos.value) payload.max_los = null;
  return payload;
});

const hasInventory = computed(
  () => Object.keys(inventoryPayload.value).length > 0
);
const hasRates = computed(() => Object.keys(ratesPayload.value).length > 0);
const hasSomething = computed(() => hasInventory.value || hasRates.value);

/**
 * Por qué no se puede aplicar todavía, en palabras. Null cuando sí se puede.
 *
 * Existe porque un botón desactivado sin explicación es un callejón sin salida: el
 * usuario completa un precio, el botón sigue gris, y no hay nada en pantalla que
 * diga que falta elegir el plan de tarifa. El orden de los chequeos es el orden en
 * que conviene resolverlos.
 */
const blockedReason = computed(() => {
  if (!rangeIsValid.value) return t('BOOKINGS.SETTINGS.BULK.EMPTY_RANGE');
  if (!hasSomething.value) return t('BOOKINGS.SETTINGS.BULK.NOTHING_TO_APPLY');
  if (hasInventory.value && !roomTypeId.value) {
    return t('BOOKINGS.SETTINGS.BULK.NEED_ROOM_TYPE');
  }
  if (hasRates.value && !ratePlanId.value) {
    return t('BOOKINGS.SETTINGS.BULK.NEED_RATE_PLAN');
  }
  return null;
});

const canSubmit = computed(() => blockedReason.value === null);

const reset = () => {
  inventory.value = { allotment: '', closed: '' };
  rates.value = {
    price_base: '',
    price_extra_adult: '',
    price_extra_child: '',
    min_los: '',
    max_los: '',
    closed_to_arrival: '',
    closed_to_departure: '',
    closed: '',
  };
  clearMaxLos.value = false;
};

const apply = async () => {
  const shared = {
    propertyId: props.property.id,
    from: range.value.from,
    to: range.value.to,
    weekdays: range.value.weekdays,
  };

  try {
    // Secuencial y no en paralelo: si la segunda falla, el mensaje tiene que poder
    // decir qué mitad quedó aplicada.
    let applied = 0;

    if (hasInventory.value) {
      const result = await store.dispatch('bookingSettings/applyInventory', {
        ...shared,
        roomTypeId: roomTypeId.value,
        inventory: inventoryPayload.value,
      });
      applied = result.applied;
    }

    if (hasRates.value) {
      const result = await store.dispatch('bookingSettings/applyRates', {
        ...shared,
        ratePlanId: ratePlanId.value,
        rates: ratesPayload.value,
      });
      applied = result.applied;
    }

    useAlert(t('BOOKINGS.SETTINGS.BULK.APPLIED', { n: applied }));
    reset();
  } catch (error) {
    const message = error?.response?.data?.message;
    useAlert(message || t('BOOKINGS.SETTINGS.BULK.FAILED'));
  }
};
</script>

<template>
  <form class="flex flex-col gap-6" @submit.prevent="apply">
    <section class="flex flex-col gap-3">
      <h3 class="text-heading-3 text-n-slate-12">
        {{ $t('BOOKINGS.SETTINGS.BULK.RANGE_TITLE') }}
      </h3>

      <div class="flex flex-wrap items-end gap-3">
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('BOOKINGS.SETTINGS.BULK.FROM') }}
          <input
            v-model="range.from"
            type="date"
            class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('BOOKINGS.SETTINGS.BULK.TO') }}
          <input
            v-model="range.to"
            type="date"
            class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
          />
        </label>
      </div>

      <p class="text-sm text-n-slate-11">
        {{ $t('BOOKINGS.SETTINGS.BULK.INCLUSIVE_HINT') }}
      </p>

      <div class="flex flex-col gap-2">
        <span class="text-sm text-n-slate-11">
          {{ $t('BOOKINGS.SETTINGS.BULK.WEEKDAYS') }}
        </span>
        <WeekdayPicker v-model="range.weekdays" />
      </div>

      <p v-if="!rangeIsValid" class="text-sm text-n-ruby-11">
        {{ $t('BOOKINGS.SETTINGS.BULK.EMPTY_RANGE') }}
      </p>
      <p v-else class="text-sm font-medium text-n-slate-12">
        {{ $t('BOOKINGS.SETTINGS.BULK.AFFECTED', { n: affectedNights }) }}
      </p>
    </section>

    <section class="flex flex-col gap-3">
      <h3 class="text-heading-3 text-n-slate-12">
        {{ $t('BOOKINGS.SETTINGS.BULK.ALLOTMENT_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ $t('BOOKINGS.SETTINGS.BULK.ALLOTMENT_HINT') }}
      </p>

      <div class="flex flex-wrap items-end gap-3">
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('BOOKINGS.SETTINGS.BULK.ROOM_TYPE') }}
          <select
            v-model="roomTypeId"
            class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
          >
            <option v-for="item in roomTypes" :key="item.id" :value="item.id">
              {{ item.name }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('BOOKINGS.SETTINGS.BULK.ALLOTMENT') }}
          <input
            v-model="inventory.allotment"
            type="number"
            min="0"
            step="1"
            class="w-28 px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
            :placeholder="$t('BOOKINGS.SETTINGS.BULK.UNCHANGED')"
          />
        </label>

        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('BOOKINGS.SETTINGS.BULK.CLOSE_ROOM_TYPE') }}
          <select
            v-model="inventory.closed"
            class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
          >
            <option value="">
              {{ $t('BOOKINGS.SETTINGS.BULK.UNCHANGED') }}
            </option>
            <!-- Abierto primero: un clic equivocado tiene que caer en el que NO
                 frena la venta. Cerrar por accidente deja el hotel sin vender y no
                 se nota hasta que alguien pregunta por esas fechas. -->
            <option value="false">
              {{ $t('BOOKINGS.SETTINGS.BULK.OPEN') }}
            </option>
            <option value="true">
              {{ $t('BOOKINGS.SETTINGS.BULK.CLOSED') }}
            </option>
          </select>
        </label>
      </div>

      <!--
        Con cero habitaciones cargadas una por una, el texto anterior ("tiene 0
        habitaciones físicas cargadas") se leía como un requisito que falta, y no lo
        es: el cupo es un número propio de cada noche y no depende de eso. Se dice
        distinto según el caso para no sembrar esa duda.
      -->
      <p v-if="selectedRoomType" class="text-sm text-n-slate-11">
        {{
          selectedRoomType.physical_room_count
            ? $t('BOOKINGS.SETTINGS.BULK.PHYSICAL_HINT', {
                n: selectedRoomType.physical_room_count,
              })
            : $t('BOOKINGS.SETTINGS.BULK.NO_PHYSICAL_HINT')
        }}
      </p>
    </section>

    <section class="flex flex-col gap-3">
      <h3 class="text-heading-3 text-n-slate-12">
        {{ $t('BOOKINGS.SETTINGS.BULK.RATES_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ $t('BOOKINGS.SETTINGS.BULK.RATES_HINT') }}
      </p>

      <p v-if="!ratePlans.length" class="text-sm text-n-amber-11">
        {{ $t('BOOKINGS.SETTINGS.BULK.NO_RATE_PLANS') }}
      </p>

      <template v-else>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11 max-w-xs">
          {{ $t('BOOKINGS.SETTINGS.BULK.RATE_PLAN') }}
          <select
            v-model="ratePlanId"
            class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
          >
            <option v-for="plan in ratePlans" :key="plan.id" :value="plan.id">
              {{ plan.name }}
            </option>
          </select>
        </label>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ $t('BOOKINGS.SETTINGS.BULK.PRICE_BASE') }}
            <input
              v-model="rates.price_base"
              type="number"
              min="0"
              step="0.01"
              class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
              :placeholder="$t('BOOKINGS.SETTINGS.BULK.UNCHANGED')"
            />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ $t('BOOKINGS.SETTINGS.BULK.PRICE_EXTRA_ADULT') }}
            <input
              v-model="rates.price_extra_adult"
              type="number"
              min="0"
              step="0.01"
              class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
              :placeholder="$t('BOOKINGS.SETTINGS.BULK.UNCHANGED')"
            />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ $t('BOOKINGS.SETTINGS.BULK.PRICE_EXTRA_CHILD') }}
            <input
              v-model="rates.price_extra_child"
              type="number"
              min="0"
              step="0.01"
              class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
              :placeholder="$t('BOOKINGS.SETTINGS.BULK.UNCHANGED')"
            />
          </label>
        </div>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ $t('BOOKINGS.SETTINGS.BULK.MIN_LOS') }}
            <input
              v-model="rates.min_los"
              type="number"
              min="1"
              step="1"
              class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
              :placeholder="$t('BOOKINGS.SETTINGS.BULK.UNCHANGED')"
            />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ $t('BOOKINGS.SETTINGS.BULK.MAX_LOS') }}
            <input
              v-model="rates.max_los"
              type="number"
              min="1"
              step="1"
              :disabled="clearMaxLos"
              class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12 disabled:opacity-50"
              :placeholder="$t('BOOKINGS.SETTINGS.BULK.UNCHANGED')"
            />
            <span class="flex items-center gap-2">
              <input v-model="clearMaxLos" type="checkbox" />
              {{ $t('BOOKINGS.SETTINGS.BULK.CLEAR_MAX_LOS') }}
            </span>
          </label>
        </div>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ $t('BOOKINGS.SETTINGS.BULK.CLOSED_TO_ARRIVAL') }}
            <select
              v-model="rates.closed_to_arrival"
              class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
            >
              <option value="">
                {{ $t('BOOKINGS.SETTINGS.BULK.UNCHANGED') }}
              </option>
              <option value="true">
                {{ $t('BOOKINGS.SETTINGS.BULK.YES') }}
              </option>
              <option value="false">
                {{ $t('BOOKINGS.SETTINGS.BULK.NO') }}
              </option>
            </select>
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ $t('BOOKINGS.SETTINGS.BULK.CLOSED_TO_DEPARTURE') }}
            <select
              v-model="rates.closed_to_departure"
              class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
            >
              <option value="">
                {{ $t('BOOKINGS.SETTINGS.BULK.UNCHANGED') }}
              </option>
              <option value="true">
                {{ $t('BOOKINGS.SETTINGS.BULK.YES') }}
              </option>
              <option value="false">
                {{ $t('BOOKINGS.SETTINGS.BULK.NO') }}
              </option>
            </select>
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ $t('BOOKINGS.SETTINGS.BULK.CLOSE_RATE_PLAN') }}
            <select
              v-model="rates.closed"
              class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
            >
              <option value="">
                {{ $t('BOOKINGS.SETTINGS.BULK.UNCHANGED') }}
              </option>
              <option value="false">
                {{ $t('BOOKINGS.SETTINGS.BULK.OPEN') }}
              </option>
              <option value="true">
                {{ $t('BOOKINGS.SETTINGS.BULK.CLOSED') }}
              </option>
            </select>
          </label>
        </div>
      </template>
    </section>

    <div class="flex flex-wrap items-center gap-3 pt-2 border-t border-n-weak">
      <button
        type="submit"
        :disabled="!canSubmit || uiFlags.isApplying"
        class="px-4 py-1.5 text-sm font-medium rounded-lg bg-n-brand text-white disabled:opacity-50"
      >
        {{ $t('BOOKINGS.SETTINGS.BULK.APPLY') }}
      </button>
      <span v-if="blockedReason" class="text-sm text-n-amber-11">
        {{ blockedReason }}
      </span>
    </div>
  </form>
</template>
